import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { PlusIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid'
import _ from 'lodash'

// Components
import { Button } from '../../components/Button'
import { DataTable } from '../../components/DataTable'
import { Modal } from '../../components/Modal'
import { PageContainer } from '../../components/PageContainer'
import { StateContainer } from '../../components/StateContainer'
import { TextEditor } from '../../components/CustomEditor'
import { TextInput } from '../../components/TextInput'

// Services
import { getClient } from '../../services/clients.service'
import {
  createClientComposite,
  deleteClientComposite,
  getClientComposites,
  getCompositeBenchmarkDataCount,
  getCompositePortfolioDataCount,
  updateClientComposite,
} from '../../services/composites.service'
import { getGlobalRules } from '../../services/global.service'

// Stores
import { ClientDashboardStoreContext } from '../../stores/ClientDashboardStore'

// Utils & Hooks
import { toast } from '../../utils/helpers'
import { usePagination, useSorting } from '../../hooks/DataTableManagement'
import { createClientRule } from '../../services/rules.service'

/**
 *
 * Composites
 *
 */
const Composites = observer(() => {
  // Context
  const { clientId } = useParams()
  const { client, setClient } = useContext(ClientDashboardStoreContext)
  const navigate = useNavigate()

  // Pagination
  const { pagination, setTotalRecords } = usePagination(10)
  const { perPage, currentPage } = pagination

  // Sorting
  const { sorting } = useSorting('name')
  const { sortedColumn } = sorting

  // State
  const [loading, setLoading] = useState(true)
  const [loadingComposites, setLoadingComposites] = useState(false)
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [error, setError] = useState(null)
  const [composites, setComposites] = useState([])
  const [showAddCompositeModal, setShowAddCompositeModal] = useState(false)
  const [globalRules, setGlobalRules] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [search, setSearch] = useState('')
  const [portfolioDataCounts, setPortfolioDataCounts] = useState({})
  const [benchmarkDataCounts, setBenchmarkDataCounts] = useState({})

  const BASE_URL = `/clients/${clientId}/composites/?`
  const searchInputRef = useRef()

  const handleSuccess = (m) => toast(m, 'success')
  const handleErrors = (m) => toast(m, 'error')

  /**
   * Gets the updated list of composites; updates pagination and portfolio data counts.
   * @param {string} url
   */
  const getUpdatedComposites = async (url) => {
    const response = await getClientComposites(url, setError, setLoadingComposites)

    if (response) {
      setTotalRecords(response.count)
      setComposites(response.results)

      // Fetch all portfolio data counts and benchmark data counts concurrently
      const portfolioCountPromises = response.results.map(composite =>
        getCompositePortfolioDataCount(clientId, composite.id, () => {}, () => {})
      )
      const benchmarkCountPromises = response.results.map(composite =>
        getCompositeBenchmarkDataCount(clientId, composite.id, () => {}, () => {})
      )

      const [portfolioCountResults, benchmarkCountResults] = await Promise.all([
        Promise.all(portfolioCountPromises),
        Promise.all(benchmarkCountPromises)
      ])

      const portfolioCounts = {}
      const benchmarkCounts = {}
      response.results.forEach((composite, idx) => {
        portfolioCounts[composite.id] = portfolioCountResults[idx]
        benchmarkCounts[composite.id] = benchmarkCountResults[idx]
      })
      setPortfolioDataCounts(portfolioCounts)
      setBenchmarkDataCounts(benchmarkCounts)
    }
  }

  useEffect(() => {
    if (clientId) {
      const getUpdatedClient = async () => {
        const [updatedClient, updatedGlobalRules] = await Promise.all([
          getClient(clientId, setError, setLoading),
          getGlobalRules(setError, () => {}),
        ])

        if (updatedClient) {
          setClient(updatedClient)
        }
        if (updatedGlobalRules) {
          setGlobalRules(updatedGlobalRules)
        }
      }

      getUpdatedClient()
    }
  }, [clientId])

  /**
   * When the filter, sort, current page, or row count changes, get the updated list of composites.
   */
  useEffect(() => {
    if (clientId) {
      getUpdatedComposites(
        `${BASE_URL}&order_by=${sortedColumn}&limit=${perPage}&page=${currentPage}&${
          search ? `q=${search}` : ''
        }`,
      )
    }
  }, [perPage, clientId, sortedColumn, currentPage, search])

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm({
    defaultValues: {
      name: '',
    },
  })

  /**
   * Handles form submission by creating a new composite for the client.
   * @param {object} data
   */
  const onSubmit = async (data, defaultRules = true) => {
    const composite = await createClientComposite(
      client.id,
      data,
      handleErrors,
      setLoading,
      () => {},
    )

    // If the composite was successfully created and we want to use the default rules
    if (composite && defaultRules) {
      const rules = { ...globalRules, periodTo: null, periodFrom: null }
      delete rules.id
      rules.composite = composite.id

      await createClientRule(clientId, rules, handleErrors, setLoading, () => {
        handleSuccess('Composite created with default rules.')
        setShowAddCompositeModal(false)
        reset()
        getUpdatedComposites(
          `${BASE_URL}&order_by=${sortedColumn}&limit=${perPage}&page=${currentPage}`,
        )
      })
    }
    // Otherwise, direct the user to the rules tab with a new rule set for this composite
    else if (composite) {
      handleSuccess('Composite created! Set up custom rules.')
      navigate(`/clients/${clientId}/settings/rules?new=true&composite=${composite.id}`)
    }
  }

  /**
   * Debounces the search input.
   */
  const searchComposites = useCallback(_.debounce(setSearch, 500), [])

  /**
   * Handles row edit completion by updating the local `composites` with new data and
   * submitting it to the backend.
   * @param {object} event
   */
  const onRowEditComplete = async ({ newData, index }) => {
    const oldComposites = [...composites]
    const updatedData = [...composites]
    updatedData[index] = newData

    // Optimistically update the data to prevent showing internal values
    setComposites(updatedData)

    await updateClientComposite(
      clientId,
      newData,
      (m) => {
        // Reset back to previous value
        setComposites(oldComposites)
        handleErrors(m)
      },
      () => {},
      (m) => handleSuccess(m),
    )
  }

  return (
    <PageContainer>
      <StateContainer error={error} loading={loading}>
        <div className="flex size-full flex-col overflow-y-auto bg-background">
          <div className="space-y-6">
            <div className="flex flex-col gap-2">
              <div className="flex w-full flex-col justify-between gap-2 sm:flex-row sm:items-center">
                <h2 className="text-xl font-semibold leading-6 text-gray-900">Composites</h2>
                <Button label="Add Composite" onClick={() => setShowAddCompositeModal(true)} />
              </div>

              <TextInput
                className="w-full rounded-full py-2.5 pl-10 pr-4 placeholder:font-normal placeholder:text-gray-600 md:w-[450px]"
                icon={
                  <MagnifyingGlassIcon className="ml-2 h-5 text-gray-dark" aria-hidden="true" />
                }
                id="search"
                endIcon={
                  searchTerm ? (
                    <Button
                      type="button"
                      onClick={() => {
                        setSearchTerm('')
                        searchComposites(null)
                        searchInputRef.current.value = ''
                      }}
                      icon={<XMarkIcon className="mr-2 h-5 text-gray-dark" aria-hidden="true" />}
                      ariaLabel="Clear search"
                      iconOnly
                    />
                  ) : null
                }
                name="search"
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  searchComposites(e.target.value)
                }}
                placeholder="Looking for something?"
                ref={searchInputRef}
                value={searchTerm}
              />
            </div>

            <DataTable
              data={composites}
              columns={[
                {
                  field: 'name',
                  header: 'Name',
                  editor: TextEditor,
                  style: { width: '100%' },
                  sortable: true,
                  sortField: 'name_sort',
                },
              ]}
              loadingDelete={loadingDelete}
              hoverEffect={false}
              loading={loadingComposites}
              pagination={pagination}
              sorting={sorting}
              onRowEditComplete={onRowEditComplete}
              onRowDeleteConfirm={(row) => {
                deleteClientComposite(clientId, row.id, handleErrors, setLoadingDelete, () => {
                  handleSuccess('Composite deleted.')
                  getUpdatedComposites(
                    `${BASE_URL}&order_by=${sortedColumn}&limit=${perPage}&page=${currentPage}`,
                  )
                })
              }}
              areRowsDeleteable
              areRowsEditable
              rowDeleteConfirm={(row) => {
                const portfolioCount = portfolioDataCounts[row.id] || 0
                const benchmarkCount = benchmarkDataCounts[row.id] || 0
                const totalCount = portfolioCount + benchmarkCount

                let deleteMessage = ''
                if (totalCount > 0) {
                  const parts = []
                  if (portfolioCount > 0) {
                    parts.push(`${portfolioCount} portfolio data record${portfolioCount === 1 ? '' : 's'}`)
                  }
                  if (benchmarkCount > 0) {
                    parts.push(`${benchmarkCount} benchmark data record${benchmarkCount === 1 ? '' : 's'}`)
                  }
                  deleteMessage = ` This will delete ${parts.join(' and ')} associated with this composite.`
                }

                return {
                  title: `Delete Composite`,
                  type: 'warning',
                  message: (
                    <div className="flex flex-col">
                      <span className="">Are you sure you want to delete the composite:</span>
                      <span className="font-semibold">{row.name}</span>
                      {deleteMessage && (
                        <div className="mt-2 rounded border-l-4 border-orange-400 bg-orange-50 p-2">
                          <span className="text-orange-800">{deleteMessage}</span>
                        </div>
                      )}
                    </div>
                  ),
                }
              }}
            />
          </div>
        </div>
      </StateContainer>

      {showAddCompositeModal && (
        <Modal
          icon={<PlusIcon className="h-6 fill-white" />}
          open
          title="Add Composite"
          loading={loading}
          onClose={() => {
            setShowAddCompositeModal(false)
            reset()
          }}
          content={
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="mt-8 flex size-full flex-col gap-4 pt-2"
            >
              <TextInput
                fullWidth
                label="Name"
                name="name"
                error={errors.name && 'This field is required'}
                {...register('name', {
                  required: true,
                })}
              />
            </form>
          }
          actions={[
            {
              type: 'submit',
              label: 'Create with Default Rules',
              onClick: handleSubmit(onSubmit),
            },
            {
              type: 'extra',
              label: 'Create with Custom Rules',
              onClick: handleSubmit((d) => onSubmit(d, false)),
            },
            {
              type: 'cancel',
              label: 'Cancel',
              onClick: () => {
                setShowAddCompositeModal(false)
                reset()
              },
            },
          ]}
        />
      )}
    </PageContainer>
  )
})

export default Composites
